import React, { useState, useEffect } from 'react';
import { processTweetData } from '../utils/tweetProcessor';
import './MobileVideoGenerator.css';

const MobileVideoGenerator = ({ onVideoGenerate, user }) => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Prevent body scroll when component is mounted to avoid conflicts
  useEffect(() => {
    // Store original body style
    const originalStyle = window.getComputedStyle(document.body).overflow;
    
    // Allow scrolling but prevent horizontal overflow
    document.body.style.overflowX = 'hidden';
    document.body.style.overflowY = 'auto';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleGenerate = async () => {
    if (!tweetUrl.trim()) {
      setError('Please enter a tweet URL');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const processedTweetData = await processTweetData(tweetUrl);
      processedTweetData.profileFile = processedTweetData.profilePicture;
      
      onVideoGenerate({
        imageData: null, // Will be generated by video component
        tweetData: processedTweetData
      });
    } catch (error) {
      console.error('Error processing tweet:', error);
      setError('Failed to process tweet. Please check the URL and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle input focus to ensure proper mobile behavior
  const handleInputFocus = (e) => {
    // Scroll input into view on mobile to prevent keyboard covering input
    setTimeout(() => {
      e.target.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center', 
        inline: 'nearest' 
      });
    }, 300);
  };

  // Clear error when user starts typing
  const handleInputChange = (e) => {
    setTweetUrl(e.target.value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className="mobile-video-generator">
      <div className="mobile-header">
        <div className="mobile-logo">
          <span className="logo-icon">✨</span>
          <span className="logo-text">twizz</span>
        </div>
        <div className="mobile-subtitle">Generate viral videos from tweets</div>
      </div>

      <div className="mobile-input-section">
        <div className="input-container">
          <label className="input-label" htmlFor="tweet-url-input">
            Tweet URL
          </label>
          <input
            id="tweet-url-input"
            type="url"
            value={tweetUrl}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder="Paste tweet URL here..."
            className="mobile-url-input"
            disabled={isGenerating}
            autoComplete="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            aria-describedby={error ? "url-error" : undefined}
          />
        </div>

        {error && (
          <div className="mobile-error" id="url-error" role="alert">
            <span className="error-icon" aria-hidden="true">⚠️</span>
            {error}
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={isGenerating || !tweetUrl.trim()}
          className="mobile-generate-btn"
          type="button"
          aria-label={isGenerating ? "Generating video..." : "Generate video from tweet"}
        >
          {isGenerating ? (
            <>
              <span className="spinner" aria-hidden="true"></span>
              Generating...
            </>
          ) : (
            <>
              <span className="btn-icon" aria-hidden="true">🎬</span>
              Generate Video
            </>
          )}
        </button>
      </div>

      <div className="mobile-info">
        <div className="info-item">
          <span className="info-icon" aria-hidden="true">⚡</span>
          <span>Generate in seconds</span>
        </div>
        <div className="info-item">
          <span className="info-icon" aria-hidden="true">📱</span>
          <span>Perfect for TikTok & Instagram</span>
        </div>
        <div className="info-item">
          <span className="info-icon" aria-hidden="true">🎯</span>
          <span>Viral-ready content</span>
        </div>
      </div>
    </div>
  );
};

export default MobileVideoGenerator; 